import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Book, ArrowLeft, Loader, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';

const AddCoursePage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if coming from admin dashboard
  const isFromAdminDashboard = location.state?.from === 'adminDashboard' || location.pathname.includes('/admin');
  
  // Form state
  const [courseForm, setCourseForm] = useState({
    name: '',
    code: '',
    description: '',
    departmentId: '',
    schoolId: user?.schoolId || ''
  });
  
  // Component state
  const [schools, setSchools] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Check if user is admin or moderator
  const isAdmin = user?.role === 'admin';
  const isModerator = user?.role === 'moderator';
  
  // Debug info
  console.log("User in AddCoursePage:", user);
  console.log("Is from admin dashboard:", isFromAdminDashboard);
  
  // Redirect if user doesn't have permission
  useEffect(() => {
    if (!user) {
      // User not loaded yet, wait
      return;
    }
    
    if (!isAdmin && !isModerator) {
      toast.error('You do not have permission to add courses');
      navigate('/courses');
      return;
    }
    
    // If admin, fetch all schools
    if (isAdmin) {
      fetchSchools();
    }
    
  }, [user, navigate]);
  
  // Fetch departments when school changes
  useEffect(() => {
    const schoolId = courseForm.schoolId;
    
    if (schoolId) {
      fetchDepartments(schoolId);
    } else {
      setDepartments([]);
    }
  }, [courseForm.schoolId]);
  
  // Fetch all schools
  const fetchSchools = async () => {
    try {
      setLoadingSchools(true);
      console.log('Fetching all schools');
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/schools`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch schools: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Schools loaded:', data.length);
      setSchools(data);
      
      // If coming from admin dashboard and user is admin, leave school unselected
      if (isFromAdminDashboard && isAdmin) {
        setCourseForm(prev => ({ ...prev, schoolId: '' }));
      }
      // Otherwise, if user is not admin or not from admin dashboard, use their school
      else if (!isAdmin || !isFromAdminDashboard) {
        // Keep using user's schoolId
      }
      
    } catch (error) {
      console.error('Error fetching schools:', error);
      setError(`Failed to load schools: ${error.message}`);
      toast.error('Failed to load schools. Please try again later.');
    } finally {
      setLoadingSchools(false);
    }
  };
  
  // Fetch departments for the selected school
  const fetchDepartments = async (schoolId) => {
    if (!schoolId) {
      setDepartments([]);
      return;
    }
    
    try {
      setLoadingDepartments(true);
      console.log('Fetching departments for school:', schoolId);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/schools/${schoolId}/departments`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch departments: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Departments loaded:', data.length);
      setDepartments(data);
      
      if (data.length === 0) {
        setError('No departments are available for this school. Please create a department first.');
      } else {
        setError(null);
      }
      
      // Reset department selection when school changes
      setCourseForm(prev => ({ ...prev, departmentId: '' }));
      
    } catch (error) {
      console.error('Error fetching departments:', error);
      setError(`Failed to load departments: ${error.message}`);
      toast.error('Failed to load departments. Please try again later.');
    } finally {
      setLoadingDepartments(false);
    }
  };
  
  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setCourseForm({
      ...courseForm,
      [name]: value
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate school selection for admins
    if (isAdmin && !courseForm.schoolId) {
      toast.error('Please select a school');
      return;
    }
    
    // Validate required fields
    if (!courseForm.name || !courseForm.code || !courseForm.departmentId) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const schoolIdToUse = courseForm.schoolId || user.schoolId;
      
      console.log('Creating course with data:', {
        ...courseForm,
        schoolId: schoolIdToUse
      });
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/courses`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...courseForm,
            schoolId: schoolIdToUse
          })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create course');
      }
      
      const newCourse = await response.json();
      
      toast.success('Course created successfully');
      
      // Redirect to appropriate page based on context
      if (isAdmin && isFromAdminDashboard) {
        navigate('/admin/courses');
      } else {
        navigate(`/courses/${newCourse.id}`);
      }
      
    } catch (error) {
      console.error('Error creating course:', error);
      toast.error(error.message || 'Failed to create course');
    } finally {
      setSubmitting(false);
    }
  };
  
  // If user lacks permissions, don't render the component at all
  if (user && !isAdmin && !isModerator) {
    return null;
  }
  
  // Determine back button destination
  const handleBackClick = () => {
    if (isAdmin && isFromAdminDashboard) {
      navigate('/admin/courses');
    } else {
      navigate('/courses');
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <button
          onClick={handleBackClick}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          <span>Back to {isAdmin && isFromAdminDashboard ? 'Admin Courses' : 'Courses'}</span>
        </button>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex items-center mb-6">
          <div className="bg-red-100 dark:bg-red-900 rounded-full p-3 text-red-600 dark:text-red-400 mr-4">
            <Book className="h-6 w-6" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Add New Course
          </h1>
        </div>
        
        {/* Display error state */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <div>
                <h3 className="text-red-800 dark:text-red-400 font-medium">Error</h3>
                <p className="text-red-700 dark:text-red-300">{error}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Loading state */}
        {(loadingSchools && isAdmin) ? (
          <div className="flex justify-center items-center p-12">
            <Loader className="h-8 w-8 text-red-500 animate-spin mr-2" />
            <span className="text-gray-600 dark:text-gray-400">Loading schools...</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* School selection (admin only) */}
            {isAdmin && (
              <div>
                <label 
                  htmlFor="schoolId" 
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  School <span className="text-red-500">*</span>
                </label>
                <select
                  id="schoolId"
                  name="schoolId"
                  value={courseForm.schoolId}
                  onChange={handleChange}
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-md py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  required
                >
                  <option value="">Select a school</option>
                  {schools.map((school) => (
                    <option key={school.id} value={school.id}>
                      {school.name} ({school.domain})
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  As an admin, you can add courses to any school
                </p>
              </div>
            )}
            
            {/* Department selection */}
            <div className={`${loadingDepartments ? 'opacity-50' : ''}`}>
              <label 
                htmlFor="departmentId" 
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Department <span className="text-red-500">*</span>
              </label>
              {loadingDepartments ? (
                <div className="flex items-center mt-2">
                  <Loader className="h-5 w-5 text-red-500 animate-spin mr-2" />
                  <span className="text-sm text-gray-500">Loading departments...</span>
                </div>
              ) : (
                <>
                  <select
                    id="departmentId"
                    name="departmentId"
                    value={courseForm.departmentId}
                    onChange={handleChange}
                    className="w-full border border-gray-300 dark:border-gray-700 rounded-md py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                    disabled={departments.length === 0 || loadingDepartments}
                  >
                    <option value="">Select a department</option>
                    {departments.map((department) => (
                      <option key={department.id} value={department.id}>
                        {department.code} - {department.name}
                      </option>
                    ))}
                  </select>
                  {departments.length === 0 && !loadingDepartments && courseForm.schoolId && (
                    <p className="mt-1 text-sm text-red-500">
                      No departments found for this school. Please create a department first.
                    </p>
                  )}
                </>
              )}
            </div>
            
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label 
                  htmlFor="code" 
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Course Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="code"
                  name="code"
                  placeholder="e.g. 101, 201, etc."
                  value={courseForm.code}
                  onChange={handleChange}
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-md py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  required
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  The course code without department prefix (e.g. "101" for CS101)
                </p>
              </div>
              
              <div>
                <label 
                  htmlFor="name" 
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Course Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="e.g. Introduction to Computer Science"
                  value={courseForm.name}
                  onChange={handleChange}
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-md py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  required
                />
              </div>
              
              <div>
                <label 
                  htmlFor="description" 
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="4"
                  placeholder="Course description (optional)"
                  value={courseForm.description}
                  onChange={handleChange}
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-md py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleBackClick}
                className="mr-4 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || (isAdmin && !courseForm.schoolId) || !courseForm.departmentId}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader className="inline h-4 w-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create Course'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddCoursePage;